'use client'

import { useState } from 'react'
import { useQueries } from '@tanstack/react-query'

import type { RouterOutputs } from '@package/api'
import { Button, Skeleton } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { BadgeCardBadge } from './badge-card'
import { BadgeDetailsModal, LockedBadgeModal } from '../../../components'
import { useSharedTRPC } from '../../../context'
import { BadgeVictoryScreen } from '../../../lib'
import { BadgeCard } from './badge-card'
import { AchievementEmpty, AchievementGridSkeleton, AchievementLoadError } from './states'

export { BadgeCard } from './badge-card'
export type { BadgeCardBadge, BadgeCardProps } from './badge-card'
export { AchievementEmpty, AchievementGridSkeleton, AchievementLoadError } from './states'

type AchievementItem = RouterOutputs['achievement']['getAll'][number]
type BadgeType = 'BASIC' | 'SPECIAL' | 'RARE'

/** A single grid cell: the badge art plus the earned achievement if the user has it. */
interface Cell {
  badge: BadgeCardBadge
  item: AchievementItem | null
}

const FILTERS: { key: BadgeType; label: string }[] = [
  { key: 'BASIC', label: 'Umum' },
  { key: 'SPECIAL', label: 'Spesial' },
  { key: 'RARE', label: 'Langka' },
]

const EMPTY_LABEL: Record<BadgeType, string> = {
  BASIC: 'Belum ada prestasi umum',
  SPECIAL: 'Belum ada prestasi spesial',
  RARE: 'Belum ada prestasi langka',
}

export interface MyAchievementProps {
  /** User ID (e.g. child) whose achievements to show */
  userId: string
  /**
   * Whether the viewer can claim badges — true only for the child viewing their own profile.
   * When false (parents / school), the view is read-only: no unclaimed notification dots, and
   * tapping any earned badge opens the details modal directly instead of the claim victory screen.
   * @default true
   */
  canClaim?: boolean
  /** Optional root class */
  className?: string
}

export function MyAchievement({ userId, canClaim = true, className }: MyAchievementProps) {
  const trpc = useSharedTRPC()

  const [earnedQuery, basicBadgesQuery] = useQueries({
    queries: [
      trpc.achievement.getAll.queryOptions({ userId }),
      trpc.achievement.getAvailableBadges.queryOptions({ badgeType: 'BASIC' }),
    ],
  })

  const [filter, setFilter] = useState<BadgeType>('BASIC')
  const [victoryOpen, setVictoryOpen] = useState(false)
  const [victoryAchievement, setVictoryAchievement] = useState<AchievementItem | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsAchievement, setDetailsAchievement] = useState<AchievementItem | null>(null)
  const [lockedOpen, setLockedOpen] = useState(false)
  const [lockedBadge, setLockedBadge] = useState<BadgeCardBadge | null>(null)

  const earned = earnedQuery.data ?? []
  const basicBadges = basicBadgesQuery.data ?? []
  const earnedByBadgeId = new Map(earned.map((a) => [a.achievementBadgeId, a]))

  const earnedOfType = (type: BadgeType) => earned.filter((a) => a.achievementBadge.badgeType === type)
  const countByType: Record<BadgeType, number> = {
    BASIC: earnedOfType('BASIC').length,
    SPECIAL: earnedOfType('SPECIAL').length,
    RARE: earnedOfType('RARE').length,
  }
  const hasUnclaimedByType: Record<BadgeType, boolean> = {
    BASIC: canClaim && earnedOfType('BASIC').some((a) => a.claimedAt == null),
    SPECIAL: canClaim && earnedOfType('SPECIAL').some((a) => a.claimedAt == null),
    RARE: canClaim && earnedOfType('RARE').some((a) => a.claimedAt == null),
  }

  const openBadge = (badge: BadgeCardBadge, item: AchievementItem | null) => {
    // Locked (not yet earned) → show progress toward the badge's conditions.
    if (!item) {
      setLockedBadge(badge)
      setLockedOpen(true)
      return
    }
    // Owner viewing an earned-but-unclaimed badge → victory screen, which claims it on their behalf.
    // Everyone else (parents / school), and any already-claimed badge → just show the details.
    if (canClaim && item.claimedAt == null) {
      setVictoryAchievement(item)
      setVictoryOpen(true)
    } else {
      setDetailsAchievement(item)
      setDetailsOpen(true)
    }
  }

  // BASIC renders every available badge (earned = coloured, rest = locked/greyed).
  // SPECIAL / RARE render only what the user has earned — unearned ones stay hidden.
  const cells: Cell[] =
    filter === 'BASIC'
      ? basicBadges.map((badge) => ({ badge, item: earnedByBadgeId.get(badge.id) ?? null }))
      : earnedOfType(filter).map((item) => ({ badge: item.achievementBadge, item }))

  const gridPending = filter === 'BASIC' ? earnedQuery.isPending || basicBadgesQuery.isPending : earnedQuery.isPending
  const gridError = filter === 'BASIC' ? earnedQuery.isError || basicBadgesQuery.isError : earnedQuery.isError

  const refetch = () => {
    void earnedQuery.refetch()
    if (filter === 'BASIC') void basicBadgesQuery.refetch()
  }

  return (
    <main className={cn('srd:mx-auto srd:flex srd:w-full srd:max-w-[1200px] srd:flex-col srd:gap-4', className)}>
      <header className='srd:flex srd:flex-col srd:gap-0.5'>
        <h2 className={cn(typographyVariants({ variant: 'title-lg' }), 'srd:text-neutral-black')}>Prestasi</h2>
        {earnedQuery.isPending ? (
          <Skeleton className='srd:h-5 srd:w-16 srd:rounded-full' />
        ) : (
          <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>{earned.length} medali</p>
        )}
      </header>

      <div className='srd:flex srd:flex-wrap srd:gap-2'>
        {FILTERS.map(({ key, label }) => {
          const active = filter === key
          return (
            <Button
              key={key}
              variant='none'
              size='sm'
              onClick={() => setFilter(key)}
              className={cn(
                'srd:relative srd:rounded-full! srd:border',
                active ? 'srd:border-blue-30 srd:bg-blue-30 srd:text-navy-80' : 'srd:border-blue-100 srd:text-grey-100',
              )}
            >
              {!earnedQuery.isPending && hasUnclaimedByType[key] && (
                <span aria-hidden className='srd:absolute srd:-right-1 srd:-top-1 srd:flex srd:size-3'>
                  <span className='srd:absolute srd:inline-flex srd:size-full srd:animate-ping srd:rounded-full srd:bg-error srd:opacity-75' />
                  <span className='srd:relative srd:inline-flex srd:size-3 srd:rounded-full srd:border-2 srd:border-neutral-white srd:bg-error' />
                </span>
              )}
              {label}: {earnedQuery.isPending ? '…' : countByType[key]}
            </Button>
          )
        })}
      </div>

      {gridPending ? (
        <AchievementGridSkeleton />
      ) : gridError ? (
        <AchievementLoadError onRetry={refetch} retrying={earnedQuery.isRefetching} />
      ) : cells.length === 0 ? (
        <AchievementEmpty label={EMPTY_LABEL[filter]} />
      ) : (
        <div className='srd:flex srd:flex-wrap srd:gap-3'>
          {cells.map(({ badge, item }) => (
            <BadgeCard
              key={badge.id}
              badge={badge}
              item={item}
              showUnclaimedDot={canClaim}
              onOpen={() => openBadge(badge, item)}
            />
          ))}
        </div>
      )}

      {victoryAchievement && (
        <BadgeVictoryScreen
          open={victoryOpen}
          onOpenChange={(open) => {
            setVictoryOpen(open)
            if (!open) setVictoryAchievement(null)
          }}
          achievement={victoryAchievement}
          userId={userId}
        />
      )}

      {detailsAchievement && (
        <BadgeDetailsModal
          open={detailsOpen}
          onOpenChange={(open) => {
            setDetailsOpen(open)
            if (!open) setDetailsAchievement(null)
          }}
          achievement={detailsAchievement}
        />
      )}

      {lockedBadge && (
        <LockedBadgeModal
          open={lockedOpen}
          onOpenChange={(open) => {
            setLockedOpen(open)
            if (!open) setLockedBadge(null)
          }}
          badge={lockedBadge}
          userId={userId}
        />
      )}
    </main>
  )
}
